import { FC, useCallback, useMemo } from 'react';
import { EntityLink } from '../entity-link';
import { computeIconSize, EntityLinkSize, EntityLinkVariant } from '../entity-link/helpers';
import UserAvatar from '../avatar/user-avatar';
import useDrawer from '@/hooks/use-drawer.hook';
import { PartnerRoleLabels, PartnerUserModel } from '@/lib/models/partner-user.model';
import Text from '../text';

interface Props {
  user: PartnerUserModel;
  partnerId?: string;
  showEmail?: boolean;
  showRole?: boolean;
  showAvatar?: boolean;
  size?: EntityLinkSize;
  variant?: EntityLinkVariant;
  onChange?: () => void;
}

const PartnerUserLink: FC<Props> = ({
  user,
  partnerId,
  showAvatar = true,
  showEmail = true,
  showRole = true,
  size = 'small',
  variant = 'outlined',
  onChange,
}) => {
  const { openDrawer } = useDrawer();

  const { id, name, email, role } = user;

  const displayName = name || email;
  const canDisplayEmail = showEmail && !!email && !!name;
  const avatarSize = useMemo(() => computeIconSize(size, canDisplayEmail), [size, canDisplayEmail]);

  const handleClick = useCallback(() => {
    openDrawer('partner-user', { id, partnerId, onChange });
  }, [openDrawer, id, partnerId, onChange]);

  const AvatarEl = showAvatar ? (
    <UserAvatar identifier={displayName} size={avatarSize} hideTooltip />
  ) : undefined;

  const RoleEl = showRole ? (
    <Text size="small" color="grey">
      {PartnerRoleLabels[role]}
    </Text>
  ) : undefined;

  return (
    <EntityLink
      image={AvatarEl}
      title={displayName}
      titleAdornment={RoleEl}
      subtext={canDisplayEmail ? email : undefined}
      onClick={handleClick}
      size={size}
      variant={variant}
      iconSize={avatarSize}
      condensed
    />
  );
};

export default PartnerUserLink;
